"use client"

import Image from "next/image"
import { useField, useFormikContext } from "formik"

import { NovaPostIntegrationFormFields } from "@/components/deliveries/providers/nova-post/nova-post-integration-form-fields"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { DeliveryIntegrationConfig } from "@/lib/deliveries/config"
import type { DeliveryProviderMetadata } from "@/lib/deliveries/providers"
import {
  createNovaPostIntegrationConfig,
  normalizeNovaPostIntegrationFormConfig,
} from "@/lib/deliveries/providers/nova-post/nova-post-defaults"
import type { DeliveryProvider } from "@/lib/deliveries/types"
import type { DeliveryIntegrationView } from "@/server-actions/delivery-integrations"
import { useTranslations } from "next-intl"


export type DeliveryIntegrationFormValues = {
  provider: DeliveryProvider | ""
  name: string
  apiKey: string
  config: DeliveryIntegrationConfig
}

export function createDeliveryIntegrationInitialValues(
  integration?: DeliveryIntegrationView,
): DeliveryIntegrationFormValues {
  if (integration) {
    return {
      provider: integration.provider,
      name: integration.name,
      apiKey: "",
      config: integration.config,
    }
  }

  const defaults = deliveryProviderFormDefaults("nova_post")
  return {
    provider: "",
    name: defaults.name,
    apiKey: "",
    config: defaults.config,
  }
}

export function normalizeDeliveryIntegrationFormConfig(
  provider: DeliveryProvider,
  config: DeliveryIntegrationConfig,
): DeliveryIntegrationConfig {
  switch (provider) {
    case "nova_post":
      return normalizeNovaPostIntegrationFormConfig(config)
    default: {
      const exhaustiveProvider: never = provider
      return exhaustiveProvider
    }
  }
}

export function ConnectDeliveryIntegrationFormFields({
  providers,
  providersLoading,
}: {
  providers: readonly DeliveryProviderMetadata[]
  providersLoading: boolean
}) {
  const { values } = useFormikContext<DeliveryIntegrationFormValues>()

  return (
    <div className="space-y-4">
      <DeliveryProviderSelect
        providers={providers}
        loading={providersLoading}
      />
      {values.provider ? (
        <DeliveryProviderFormFields
          provider={values.provider}
          apiKeyMode="required"
        />
      ) : null}
    </div>
  )
}

export function EditDeliveryIntegrationFormFields({
  integrationId,
}: {
  integrationId: string
}) {
  const { values } = useFormikContext<DeliveryIntegrationFormValues>()
  if (!values.provider) return null
  return (
    <DeliveryProviderFormFields
      provider={values.provider}
      apiKeyMode="optional-replacement"
      integrationId={integrationId}
    />
  )
}

function DeliveryProviderFormFields({
  provider,
  apiKeyMode,
  integrationId,
}: {
  provider: DeliveryProvider
  apiKeyMode: "required" | "optional-replacement"
  integrationId?: string
}) {
  switch (provider) {
    case "nova_post":
      return (
        <NovaPostIntegrationFormFields
          apiKeyMode={apiKeyMode}
          integrationId={integrationId}
        />
      )
    default: {
      const exhaustiveProvider: never = provider
      return exhaustiveProvider
    }
  }
}

function DeliveryProviderSelect({
  providers,
  loading,
}: {
  providers: readonly DeliveryProviderMetadata[]
  loading: boolean
}) {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryIntegrationForm")

  const [field, meta] = useField<DeliveryProvider | "">("provider")
  const { setFieldTouched, setFieldValue } = useFormikContext<
    DeliveryIntegrationFormValues
  >()
  const selectedProvider = providers.find(
    provider => provider.provider === field.value,
  )
  const error = meta.touched && meta.error ? meta.error : null

  return (
    <div className="space-y-1.5">
      <Label htmlFor="delivery-provider"><span key={tUi("integration")}>{tUi("integration")}</span></Label>
      <Select
        disabled={loading || providers.length === 0}
        value={field.value}
        onValueChange={value => {
          const provider = providers.find(item => item.provider === value)
          if (!provider) return
          const defaults = deliveryProviderFormDefaults(provider.provider)
          void setFieldValue("provider", provider.provider)
          void setFieldValue("name", defaults.name)
          void setFieldValue("apiKey", "")
          void setFieldValue("config", defaults.config)
          void setFieldTouched("provider", true, false)
        }}
      >
        <SelectTrigger id="delivery-provider" aria-invalid={Boolean(error)}>
          <SelectValue
            placeholder={loading ? tUi("loadingIntegrations") : tUi("selectAnIntegration")}
          >
            {selectedProvider ? (
              <DeliveryProviderOption provider={selectedProvider} compact />
            ) : null}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {providers.map(provider => (
            <SelectItem key={provider.provider} value={provider.provider}>
              <DeliveryProviderOption provider={provider} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error ? <p className="text-xs font-medium text-destructive">{error}</p> : null}
    </div>
  )
}

function deliveryProviderFormDefaults(provider: DeliveryProvider): {
  name: string
  config: DeliveryIntegrationConfig
} {
  switch (provider) {
    case "nova_post":
      return {
        name: "Nova Post",
        config: createNovaPostIntegrationConfig(),
      }
    default: {
      const exhaustiveProvider: never = provider
      return exhaustiveProvider
    }
  }
}

function DeliveryProviderOption({
  provider,
  compact = false,
}: {
  provider: DeliveryProviderMetadata
  compact?: boolean
}) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Image
        src={provider.iconPath}
        alt=""
        width={24}
        height={24}
        className="h-6 w-6 shrink-0 rounded-sm"
      />
      <span className="min-w-0 text-left">
        <span className="block truncate" translate="no">
          {provider.displayName}
        </span>
        {!compact ? (
          <span className="block truncate text-xs text-muted-foreground">
            {provider.description}
          </span>
        ) : null}
      </span>
    </span>
  )
}
